import React, { useState } from 'react';
import {
  CreditCard,
  Search,
  Check,
  ArrowRight,
  MessageSquare,
  Sparkles,
  Phone,
  Clock,
  Layers,
  FileSpreadsheet,
  Palette,
  Server,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { OFFICIAL_PRICE_CATALOGUE, PricePackage } from '../data/siteData';

interface PriceCatalogueSectionProps {
  onOpenBooking: (serviceName?: string) => void;
  onOpenPaybill?: (amount?: number, title?: string) => void;
  onOrderPlan?: (planId?: string) => void;
}

export const PriceCatalogueSection: React.FC<PriceCatalogueSectionProps> = ({
  onOpenBooking,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'websites' | 'systems' | 'design' | 'hosting'>('websites');
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDropdownId, setSelectedDropdownId] = useState<string>('');

  const filteredItems = OFFICIAL_PRICE_CATALOGUE.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Top 3 items displayed directly as cards
  const initialCards = filteredItems.slice(0, 3);
  // Remaining items accessible via dropdown or expanded view
  const remainingItems = filteredItems.slice(3);

  // If user searched or toggled expanded, show all filtered items
  const cardsToRender = isExpanded || searchQuery.trim() !== '' ? filteredItems : initialCards;

  // Selected item from dropdown if user chooses one
  const dropdownSelectedItem = OFFICIAL_PRICE_CATALOGUE.find((it) => it.id === selectedDropdownId);

  const categories = [
    { id: 'websites', label: 'Website Packages (12)', icon: Layers, count: 12 },
    { id: 'systems', label: 'Digital Systems (3)', icon: FileSpreadsheet, count: 3 },
    { id: 'design', label: 'Design Services (8)', icon: Palette, count: 8 },
    { id: 'hosting', label: 'Hosting & Management (4)', icon: Server, count: 4 },
    { id: 'all', label: 'All Packages (27)', icon: Sparkles, count: 27 },
  ];

  const handleCategoryChange = (catId: any) => {
    setSelectedCategory(catId);
    setSelectedDropdownId('');
  };

  const renderCard = (item: PricePackage, isHighlighted = false) => (
    <div
      key={item.id}
      className={`rounded-2xl border p-6 flex flex-col justify-between bg-white transition-all hover:shadow-lg ${
        isHighlighted
          ? 'border-[#b88e4c] ring-2 ring-[#b88e4c]/50 shadow-md relative'
          : item.popular
          ? 'border-[#b88e4c] ring-1 ring-[#b88e4c]/30 shadow-xs relative'
          : 'border-slate-300'
      }`}
    >
      {item.popular && (
        <div className="absolute -top-3 right-6 bg-[#946726] text-white font-mono text-xs font-extrabold uppercase px-2.5 py-0.5 rounded-full tracking-wider shadow-xs">
          Most Popular
        </div>
      )}

      {isHighlighted && (
        <div className="absolute -top-3 left-6 bg-[#0077b5] text-white font-mono text-xs font-extrabold uppercase px-2.5 py-0.5 rounded-full tracking-wider shadow-xs">
          Dropdown Selected
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700">
            {item.categoryLabel}
          </span>
          <span className="text-xs font-mono text-[#0077b5] bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
            {item.turnaround}
          </span>
        </div>

        <h3 className="text-xl font-bold text-slate-950 font-serif-heading mb-2">
          {item.name}
        </h3>

        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed mb-4 min-h-[40px]">
          {item.description}
        </p>

        {/* Price Display */}
        <div className="mb-4 pb-4 border-b border-slate-100 flex items-baseline justify-between">
          <div>
            <span className="text-2xl sm:text-3xl font-extrabold text-slate-950 font-serif-heading tabular-nums">
              {item.priceDisplay}
            </span>
            {item.pricePeriod && (
              <span className="text-xs text-slate-600 font-mono ml-1.5">
                /{item.pricePeriod}
              </span>
            )}
          </div>
          {item.lipaPolePole && (
            <span className="text-xs font-mono font-bold bg-amber-50 text-[#946726] border border-amber-300 px-2 py-0.5 rounded">
              Lipa Pole Pole
            </span>
          )}
        </div>

        {/* Features List */}
        <ul className="space-y-2.5 mb-6 text-xs text-slate-800">
          {item.features.map((feat, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" />
              <span>{feat}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 pt-3 border-t border-slate-100">
        <button
          onClick={() => onOpenBooking(item.name)}
          className="w-full py-3 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer min-h-[44px]"
        >
          <span>Order / Inquire Now</span>
          <ArrowRight className="h-3.5 w-3.5 text-[#b88e4c]" />
        </button>

        <a
          href={`https://wa.me/254114646341?text=${encodeURIComponent(
            `Hello Thinkers Arcade, I would like to order or ask about the ${item.name} (${item.priceDisplay}). Please advise on the timeline and onboarding.`
          )}`}
          target="_blank"
          rel="noreferrer"
          className="w-full py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer min-h-[44px]"
        >
          <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
          <span>Chat on WhatsApp</span>
        </a>
      </div>
    </div>
  );

  return (
    <section id="pricing" className="py-16 sm:py-20 bg-[#faf9f6] text-slate-900 border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 text-left">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#946726] bg-amber-500/10 border border-amber-600/20 px-3 py-1 rounded-full mb-3">
              <CreditCard className="h-3.5 w-3.5 text-[#946726]" />
              <span>Official 2026 Price Catalogue</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-950 font-serif-heading">
              Transparent packages built to win you sales.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed font-sans">
              No hidden costs or surprise renewal fees. Showing top packages below — choose any from the dropdown or split project fees in <strong>3 easy installments (Lipa Pole Pole)</strong>.
            </p>
          </div>

          {/* Quick Paybill Card */}
          <div className="bg-white border border-slate-300 rounded-2xl p-4 shadow-xs shrink-0 md:max-w-xs text-left">
            <div className="flex items-center gap-2 mb-1">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-800">
                Lipa na M-Pesa Destination
              </span>
            </div>
            <div className="text-xs font-bold text-slate-900 font-mono">
              Paybill: <span className="text-[#946726] font-extrabold text-sm">542542</span> · Acc: <span className="text-[#0077b5] font-extrabold text-sm">01550</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Account Name: I&amp;M (Hosting account)
            </div>
          </div>
        </div>

        {/* Filter Navigation & Search Bar */}
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            
            {/* Accessible Tab Buttons (Min 44px Touch Targets) */}
            <div className="flex flex-wrap items-center gap-2 bg-slate-200/90 p-1.5 rounded-2xl">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer min-h-[44px] ${
                      isActive
                        ? 'bg-white text-slate-950 shadow-sm'
                        : 'text-slate-700 hover:text-slate-950 hover:bg-white/60'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-[#946726]' : 'text-slate-500'}`} />
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Accessible Search Input with explicit label */}
            <div className="relative sm:w-80">
              <label htmlFor="package-catalog-search" className="sr-only">
                Search price catalogue
              </label>
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                id="package-catalog-search"
                name="package-catalog-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search packages (e.g. Starter, Shopify, Logo)..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm text-slate-900 placeholder-slate-500 focus:outline-hidden focus:border-[#946726] focus:ring-1 focus:ring-[#946726] min-h-[44px]"
              />
            </div>
          </div>
        </div>

        {/* Product Cards Grid: Top 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
          {cardsToRender.map((item) => renderCard(item))}
        </div>

        {/* The Rest via Dropdown & Accordion Expander */}
        {!isExpanded && searchQuery.trim() === '' && remainingItems.length > 0 && (
          <div className="rounded-2xl border border-slate-300 bg-white p-5 sm:p-6 shadow-xs text-left space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <label htmlFor="remaining-packages-dropdown" className="text-xs font-mono font-bold uppercase tracking-wider text-slate-700 block mb-1">
                  View More ({remainingItems.length} More Packages in {categories.find((c) => c.id === selectedCategory)?.label})
                </label>
                <p className="text-xs text-slate-600">
                  Select any service from the dropdown below to view full deliverables, turnaround, and pricing:
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-slate-50 hover:bg-slate-100 text-xs font-bold text-slate-800 transition-colors cursor-pointer min-h-[44px] shrink-0"
              >
                <span>Expand All ({filteredItems.length}) Cards</span>
                <ChevronDown className="h-4 w-4 text-slate-600" />
              </button>
            </div>

            {/* Dropdown Select Element */}
            <div className="relative">
              <select
                id="remaining-packages-dropdown"
                name="remaining-packages-dropdown"
                aria-label="Select from remaining packages"
                value={selectedDropdownId}
                onChange={(e) => setSelectedDropdownId(e.target.value)}
                className="w-full py-3 px-4 rounded-xl border border-slate-300 bg-slate-50 text-xs sm:text-sm text-slate-900 font-medium focus:outline-hidden focus:border-[#946726] focus:ring-1 focus:ring-[#946726] cursor-pointer min-h-[44px]"
              >
                <option value="">-- Choose from {remainingItems.length} additional packages --</option>
                {remainingItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} — {item.priceDisplay} ({item.turnaround})
                  </option>
                ))}
              </select>
            </div>

            {/* If an item is selected in the dropdown, render its full interactive card right here */}
            {dropdownSelectedItem && (
              <div className="pt-4 border-t border-slate-200">
                <div className="max-w-lg mx-auto">
                  {renderCard(dropdownSelectedItem, true)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* When Expanded, Show Collapse Button */}
        {isExpanded && searchQuery.trim() === '' && (
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-xs font-bold text-slate-800 transition-colors cursor-pointer min-h-[44px] shadow-xs"
            >
              <span>Collapse to Top 3 Packages</span>
              <ChevronUp className="h-4 w-4 text-slate-600" />
            </button>
          </div>
        )}

        {/* Lipa Pole Pole Explainer Banner */}
        <div className="rounded-3xl bg-slate-950 text-white p-6 sm:p-8 border border-slate-800 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 text-left">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#b88e4c]">
              <Clock className="h-3.5 w-3.5" />
              <span>Lipa Pole Pole Installment Program</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-serif-heading text-white">
              Launch your project now. Split payments into 3 monthly parts.
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans">
              We understand cash flow. Pay a deposit to begin development immediately, the second part upon client review, and the final installment after your website or system is live. No complicated loans or interest.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <button
              onClick={() => onOpenBooking('Lipa Pole Pole Inquiry')}
              className="px-6 py-3 rounded-xl bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer min-h-[44px]"
            >
              <span>Apply for Lipa Pole Pole</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="tel:+254114646341"
              className="px-5 py-3 rounded-xl border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200 font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer min-h-[44px]"
            >
              <Phone className="h-4 w-4 text-[#009fe3]" />
              <span>Call: +254 114 646 341</span>
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};
